import React from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  DownloadCloud,
  Search
} from 'lucide-react';
import styled from '../../styled';
import { AnimatedNumber, useInView, usePrefersReducedMotion } from '../motion';
import {
  MerchantDot,
  MockupDots,
  ProductMockupBody,
  ProductMockupFrame,
  ProductMockupHeader,
  ProductMockupLabel
} from './shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const PageTitle = styled('div')`
  display: flex;
  align-items: baseline;
  gap: 8px;

  .title {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
  }

  .count {
    font-size: 11px;
    color: #94a3b8;
  }
`;

const ToolButton = styled('span')<{ subtle?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: ${(p) => (p.subtle ? '#64748b' : '#334155')};
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
  white-space: nowrap;
`;

const FilterRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SearchBox = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  min-width: 150px;
`;

const FilterDropdown = styled(ToolButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
`;

const filterSkeletonWidths = ['52px', '44px', '48px'];

const RightTools = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;

  .pagination {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #64748b;
  }
`;

const SummaryCard = styled('div')`
  display: flex;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  padding: 12px 4px;
  margin-bottom: 12px;
`;

const SummaryMetric = styled('div')`
  flex: 1 0 auto;
  min-width: 84px;
  padding: 0 14px;

  .label {
    font-size: 10px;
    color: #94a3b8;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .value {
    font-size: 13.5px;
    font-weight: 700;
    color: #0e1c2b;
    white-space: nowrap;
  }
`;

const TableCard = styled('div')`
  background: #fff;
  border: 1px solid #ececea;
  border-radius: 10px;
  overflow-x: auto;
`;

const Table = styled('table')`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: right;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 12px 10px 8px;
    white-space: nowrap;

    &.left {
      text-align: left;
    }
  }

  td {
    padding: 10px;
    color: #334155;
    white-space: nowrap;
    text-align: right;
    vertical-align: middle;

    &.left {
      text-align: left;
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const CampaignCell = styled('div')`
  .name {
    font-size: 12px;
    font-weight: 600;
    color: #0e1c2b;
    margin-bottom: 5px;
  }

  .merchant {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    color: #475569;
  }
`;

const SkeletonBar = styled('span')<{ width: string }>`
  display: inline-block;
  height: 9px;
  border-radius: 100px;
  background: #eef1f4;
  width: ${(p) => p.width};
  vertical-align: middle;
`;

const SkeletonCampaignCell = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SkeletonRow = styled('tr')`
  border-top: 1px solid #f4f4f2;
`;

const wayfairRow = {
  name: 'Wayfair - Oct – Dec 2026',
  merchant: 'Wayfair',
  initial: 'W',
  color: '#7b0051',
  start: 'Oct 1, 2026',
  end: 'Jan 1, 2027',
  goal: '$150,000',
  pctGoal: '92.14%',
  sales: '$138,204.11',
  roas: '3.2x',
  orders: '412',
  items: '486',
  earnings: '$12,442.05',
  pages: '218'
};

const skeletonMetricWidths = [
  ['58px', '62px', '52px', '48px', '68px', '36px', '44px', '40px', '52px', '36px'],
  ['54px', '58px', '48px', '44px', '64px', '32px', '40px', '36px', '48px', '34px'],
  ['56px', '60px', '50px', '46px', '66px', '34px', '42px', '38px', '50px', '32px'],
  ['52px', '56px', '46px', '42px', '62px', '30px', '38px', '34px', '46px', '30px']
];
const summaryMetrics = [
  { label: 'Flat spend', value: 180, format: (n: number) => `$${n.toFixed(2)}K` },
  { label: 'Pageviews', value: 513867 },
  { label: 'Clicks', value: 38540 },
  { label: 'CTR', value: 7.5, format: (n: number) => `${n.toFixed(1)}%` },
  { label: 'Orders', value: 1649 },
  { label: 'Conv. rate', value: 4.3, format: (n: number) => `${n.toFixed(1)}%` },
  { label: 'Earnings', value: 55.43, format: (n: number) => `$${n.toFixed(2)}K` },
  { label: 'Sales volume', value: 581.43, format: (n: number) => `$${n.toFixed(2)}K` }
];

const skeletonCampaignWidths = ['132px', '118px', '124px', '110px'];

export const CrossCampaignReportMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.2 });

  return (
    <ProductMockupFrame ref={ref}>
      <ProductMockupHeader>
        <ProductMockupLabel>Amplify · Reporting</ProductMockupLabel>
        <MockupDots>
          <span />
          <span />
          <span />
        </MockupDots>
      </ProductMockupHeader>
      <ProductMockupBody>
        <TitleRow>
          <PageTitle>
            <span className="title">Campaign report</span>
            <span className="count">38 campaigns</span>
          </PageTitle>
          <ToolButton subtle>
            Last 30 days <ChevronDown size={12} />
          </ToolButton>
        </TitleRow>

        <FilterRow>
          <SearchBox>
            <Search size={12} /> Search campaigns
          </SearchBox>
          {filterSkeletonWidths.map((width) => (
            <FilterDropdown key={width}>
              <SkeletonBar width={width} />
              <ChevronDown size={12} color="#cbd5e1" />
            </FilterDropdown>
          ))}
          <RightTools>
            <ToolButton subtle>
              <DownloadCloud size={12} /> Export
            </ToolButton>
            <span className="pagination">
              <ChevronLeft size={12} color="#cbd5e1" /> 1{' '}
              <ChevronRight size={12} />
            </span>
          </RightTools>
        </FilterRow>

        <SummaryCard>
          {summaryMetrics.map((m) => (
            <SummaryMetric key={m.label}>
              <div className="label">{m.label}</div>
              <div className="value">
                <AnimatedNumber
                  value={m.value}
                  start={inView}
                  format={m.format}
                />
              </div>
            </SummaryMetric>
          ))}
        </SummaryCard>

        <TableCard>
          <Table>
            <thead>
              <tr>
                <th className="left">Campaign name</th>
                <th className="left">Campaign start</th>
                <th className="left">Campaign end</th>
                <th>Goal</th>
                <th>% Goal</th>
                <th>Sales volume</th>
                <th>ROAS</th>
                <th>Orders</th>
                <th>Items sold</th>
                <th>Earnings</th>
                <th>Pages</th>
              </tr>
            </thead>
            <tbody>
              <AnimatedRow visible={inView} delayMs={200} reduced={reduced}>
                <td className="left">
                  <CampaignCell>
                    <div className="name">{wayfairRow.name}</div>
                    <div className="merchant">
                      <MerchantDot bg={wayfairRow.color}>
                        {wayfairRow.initial}
                      </MerchantDot>
                      {wayfairRow.merchant}
                    </div>
                  </CampaignCell>
                </td>
                <td className="left">{wayfairRow.start}</td>
                <td className="left">{wayfairRow.end}</td>
                <td>{wayfairRow.goal}</td>
                <td>{wayfairRow.pctGoal}</td>
                <td>{wayfairRow.sales}</td>
                <td>{wayfairRow.roas}</td>
                <td>{wayfairRow.orders}</td>
                <td>{wayfairRow.items}</td>
                <td>{wayfairRow.earnings}</td>
                <td>{wayfairRow.pages}</td>
              </AnimatedRow>
              {skeletonMetricWidths.map((widths, rowIndex) => (
                <SkeletonRow key={rowIndex}>
                  <td className="left">
                    <SkeletonCampaignCell>
                      <SkeletonBar width={skeletonCampaignWidths[rowIndex]} />
                      <SkeletonBar width="72px" />
                    </SkeletonCampaignCell>
                  </td>
                  {widths.map((width, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={cellIndex < 2 ? 'left' : undefined}
                    >
                      <SkeletonBar width={width} />
                    </td>
                  ))}
                </SkeletonRow>
              ))}
            </tbody>
          </Table>
        </TableCard>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
